import * as React from 'react';
import styled from 'styled-components';
import {toast} from 'react-toastify';
import {FileObject} from 'api/library';
import {createLesson, editLesson, getLessonByID} from 'api/lesson/requests';
import {LessonObject} from 'api/lesson/types';
import LessonBuilderContext, {LessonBuilderContextValue} from './LessonBuilderContext';
import Container from 'containers/Container';
import PreviewPicker from 'components/PreviewPicker';
import InputTitle from 'components/InputTitle';
import InputDescription from 'components/InputDescription';
import Content from './components/Content';
import BuilderFooter from 'components/BuilderFooter';
import Button, {ButtonVariant} from 'components/Button';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import ButtonLink from '../ButtonLink';
import moment from "moment";
import {transparentize} from "polished";

export type LessonBuilderProps = {
  lessonId?: LessonObject['id'];
  onCreated?(id: LessonObject['id']): void;
  onUpdate?(id: LessonObject['id']): void;
};

export interface LessonBuilderState {
  lesson: Partial<LessonObject>;
}

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  justify-content: space-between;
  flex-direction: column;
  display: flex;
`;

const DateCreated = styled.div`
  margin-bottom: 10px;
  text-align: right;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: ${({theme}) => transparentize(.6, theme.variables.color)}
`;

const Header = styled.header`
  margin-bottom: 20px;
  column-gap: 20px;
  align-items: flex-start;
  grid-template-columns: minmax(min-content, 300px) 1fr;
  grid-auto-flow: column;
  display: grid;
`;

const PreviewContainer = styled.div`
  grid-row: span 2;
`;

const HeaderContent = styled.div`
  row-gap: 10px;
  grid-template-rows: max-content 1fr;
  display: grid;
  min-height: 170px;
`;

const Buttons = styled.div`
  gap: 12px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

/**
 * The component create or update lesson object.
 */
export default class LessonBuilder extends
  React.Component<LessonBuilderProps, LessonBuilderState> {
  /**
   * Construct the props.
   * @param {LessonBuilderProps} props
   */
  constructor(props: LessonBuilderProps) {
    super(props);

    this.state = {
      lesson: this.defaultLesson(),
    };

    this.setLesson = this.setLesson.bind(this);
  }

  componentDidMount() {
    if (this.props.lessonId !== undefined) {
      getLessonByID(this.props.lessonId).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          this.setState((prevState) => ({
            ...prevState,
            lesson: response,
          }));
        }
      })
    }
  }

  /**
   * Get default lesson object.
   * @return {Partial<LessonObject>}
   */
  defaultLesson(): Partial<LessonObject> {
    return {} as Partial<LessonObject>;
  }

  /**
   * This function changes the current lesson object.
   * @param {Partial<LessonObject>} lesson
   */
  setLesson(lesson: Partial<LessonObject>): void {
    this.setState((prevState) => {
      return {
        ...prevState,
        lesson,
      };
    });
  }

  /**
   * Render the builder.
   * @return {JSX.Element}
   */
  render() {
    const {
      props,
      state,
      setLesson,
    } = this;

    const {
      lesson,
    } = state;

    /**
     * Is the course created.
     * @return {boolean}
     */
    function isCreated(): boolean {
      return Boolean(lesson.id);
    }

    /**
     * render created date
     * @return {React.ReactElement | void}
     */
    function renderDateCreated(): React.ReactElement | void {
      if (isCreated()) {
        return (
          <DateCreated>
            Создан {moment(lesson.date).format('DD.MM.YYYY | HH:MM')}
          </DateCreated>
        )
      }
    }

    /**
     * Render the header
     * @return {JSX.Element}
     */
    function renderHeader(): JSX.Element {
      /**
       * Render preview
       * @return {JSX.Element}
       */
      function renderPreview(): JSX.Element {
        /**
         * This change event handler.
         * @param {FileObject} file
         */
        function handleChange(file: FileObject): void {
          setLesson({
            ...lesson,
            preview: {
              id: file.id,
              type: file.type,
              url: file.url,
            },
          });
        }

        return (
          <PreviewContainer>
            <PreviewPicker
              preview={lesson.preview}
              onChange={handleChange}
            />
          </PreviewContainer>
        );
      }

      /**
       * Render title
       * @return {JSX.Element}
       */
      function renderTitle(): JSX.Element {
        /**
         * Handle title blur
         * @param {React.FocusEvent<HTMLTextAreaElement>} event
         */
        function handleBlur(
            event: React.FocusEvent<HTMLTextAreaElement>,
        ): void {
          setLesson({
            ...lesson,
            title: event.target.value,
          });
        }

        return (
          <InputTitle
            placeholder="Введите название урока"
            defaultValue={lesson.title}
            onBlur={handleBlur}
          />
        );
      }

      /**
       * Render description
       * @return {JSX.Element}
       */
      function renderDescription(): JSX.Element {
        /**
         * Handle description blur
         * @param {React.FocusEvent<HTMLTextAreaElement>} event
         */
        function handleBlur(
            event: React.FocusEvent<HTMLTextAreaElement>,
        ): void {
          setLesson({
            ...lesson,
            description: event.target.value,
          });
        }

        return (
          <InputDescription
            placeholder="Введите описание урока"
            defaultValue={lesson.description}
            onBlur={handleBlur}
          />
        );
      }

      return (
        <Header>
          {renderPreview()}
          <HeaderContent>
            {renderTitle()}
            {renderDescription()}
          </HeaderContent>
        </Header>
      );
    }

    /**
     * Render footer.
     * @return {JSX.Element}
     */
    function renderFooter(): JSX.Element {
      /**
       * Is the lesson data valid.
       * @return {boolean}
       */
      function isLessonValid(): boolean {
        if (!lesson.title?.length) {
          toast.error('Введите название урока');
          return false;
        }

        if (!lesson.description?.length) {
          toast.error('Введите описание урока');
          return false;
        }

        return true;
      }

      /**
       * Render create button.
       * @return {JSX.Element}
       */
      function renderCreateButton(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          if (isLessonValid()) {
            createLesson(lesson as LessonObject).then((response) => {
              validateResponse(response);

              if (response && !isError(response)) {
                toast.success('Создан новый урок');
                props.onCreated && props.onCreated(response.id);
              }
            });
          }
        }

        return (
          <Button
            type="button"
            variant={ButtonVariant.Primary}
            onClick={handleClick}
          >
            Создать
          </Button>
        );
      }

      /**
       * Render update button.
       * @return {JSX.Element}
       */
      function renderUpdateButton(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          if (isLessonValid()) {
            editLesson(lesson as LessonObject).then((response) => {
              validateResponse(response);

              if (response && !isError(response)) {
                toast.success('Урок обновлен');
                props.onUpdate && props.onUpdate(response.id);
              }
            });
          }
        }

        return (
          <Button
            type="button"
            variant={ButtonVariant.Primary}
            onClick={handleClick}
          >
            Сохранить
          </Button>
        );
      }

      /**
       * Render preview button.
       * @return {JSX.Element}
       */
      function renderPreviewButton(): JSX.Element {
        return (
          <ButtonLink
            target="_blank"
            rel="noopener noreferrer"
            href={`/lessons/${lesson.id}`}
            variant={ButtonVariant.Outline}
          >
            Предпросмотр
          </ButtonLink>
        );
      }

      return (
        <BuilderFooter>
          {isCreated() ? (
            <Buttons>
              {renderPreviewButton()}
              {renderUpdateButton()}
            </Buttons>
          ) : (
            renderCreateButton()
          )}
        </BuilderFooter>
      );
    }

    const {Provider} = LessonBuilderContext;

    /**
     * Get the context value
     * @return {CourseBuilderContextValue}
     */
    function getContextValue(): LessonBuilderContextValue {
      return {
        lesson,
        setLesson,
      };
    }

    return (
      <Provider value={getContextValue()}>
        <Wrapper>
          <Container>
            {renderDateCreated()}
            {renderHeader()}
            <Content />
          </Container>
          {renderFooter()}
        </Wrapper>
      </Provider>
    );
  }
}
